/**
 * Reading the browser's camera/microphone permission state outside React.
 *
 * The capture pipeline needs one fact that `getUserMedia` itself never tells it:
 * whether the browser is about to ask the customer a question. A pending
 * permission dialog and an unresponsive device look identical from the promise —
 * both are "still not resolved" — and treating the first like the second is what
 * cut a customer off mid-question after ten seconds.
 *
 * `useMediaPermissions` answers the same question for React screens; this module
 * is the plain-function form the provider can call.
 */

import type { MediaKind, MediaRequest } from '../providers/types';

/** How often the label fallback re-checks for a grant. */
const GRANT_POLL_INTERVAL_MS = 1_000;

export function kindsForRequest(request: MediaRequest): MediaKind[] {
  return request === 'both' ? ['camera', 'microphone'] : [request];
}

export function requestForKinds(kinds: readonly MediaKind[]): MediaRequest | undefined {
  if (kinds.length === 0) return undefined;
  if (kinds.length > 1) return 'both';
  return kinds[0];
}

/** The device-enumeration kind that backs a permission. */
function deviceKind(kind: MediaKind): MediaDeviceInfo['kind'] {
  return kind === 'camera' ? 'videoinput' : 'audioinput';
}

/**
 * Query one permission, or report that this browser cannot be asked.
 *
 * Firefox is the reason for `'unsupported'`: it rejects `permissions.query` for
 * camera and microphone outright, so callers must have a second way to tell.
 */
export async function queryPermissionState(
  kind: MediaKind
): Promise<PermissionState | 'unsupported'> {
  const status = await queryPermissionStatus(kind);
  return status ? status.state : 'unsupported';
}

async function queryPermissionStatus(kind: MediaKind): Promise<PermissionStatus | null> {
  try {
    if (!navigator.permissions?.query) return null;
    return await navigator.permissions.query({ name: kind as PermissionName });
  } catch {
    // Firefox throws TypeError for these names; older engines have no API at all.
    return null;
  }
}

export type CapturePermission = PermissionState | 'unsupported';

/**
 * One answer for several devices. A certain `denied` on any of them outweighs a
 * browser that cannot be asked about another: the attempt fails either way.
 */
export function combinePermissionStates(states: readonly CapturePermission[]): CapturePermission {
  if (states.includes('denied')) return 'denied';
  if (states.length === 0 || states.includes('unsupported')) return 'unsupported';
  if (states.includes('prompt')) return 'prompt';
  return 'granted';
}

/** The combined permission state of every requested device, read once. */
export async function queryCapturePermission(
  kinds: readonly MediaKind[]
): Promise<CapturePermission> {
  return combinePermissionStates(await Promise.all(kinds.map(queryPermissionState)));
}

/**
 * Whether a `denied` read from `permissions.query` means "asking again is
 * pointless" in this browser.
 *
 * Chrome (and every Chromium) and Firefox answer a blocked origin's
 * `getUserMedia` without a dialog, and each such refusal only counts towards
 * keeping the block. Safari is the exception: it forgets an in-page refusal the
 * moment the customer clicks again, and only a request issued synchronously
 * from that click is asked at all — so there the click must always reach
 * `getUserMedia`, whatever the permission reads.
 */
export function browserKeepsRefusal(userAgent: string = navigator.userAgent): boolean {
  const isWebKitOnly = userAgent.includes('Safari') && !/Chrome|Chromium|Edg|Firefox/.test(userAgent);
  return !isWebKitOnly;
}

/** Chrome's wording for a permission dialog closed without an answer. */
export function isDismissalMessage(message: unknown): boolean {
  return typeof message === 'string' && /dismissed/i.test(message);
}

/** Chrome's wording for a refusal by the operating system, not the browser. */
export function isSystemDenialMessage(message: unknown): boolean {
  return typeof message === 'string' && /denied by system/i.test(message);
}

/**
 * Follow the permission of the requested devices while the caller cares.
 *
 * `onGranted` fires once, on a *change* to granted — the customer allowing
 * access from the address bar or site settings while our modal is up. A state
 * that already reads granted does not fire it: an attempt that just failed
 * with the permission in hand failed for another reason, and retrying it
 * unprompted would loop.
 *
 * Where `permissions.query` cannot answer, nothing is watched. Polling with
 * `getUserMedia` instead would issue a request without a click, which Safari
 * refuses silently and Chrome counts towards the block.
 *
 * `isBlocked` reads the live `PermissionStatus` synchronously, so a click
 * handler can decide without an `await` between the click and `getUserMedia`.
 * It is false until the statuses have arrived and wherever they never do.
 */
export interface PermissionWatch {
  isBlocked: () => boolean;
  cancel: () => void;
}

export function watchCapturePermission(
  kinds: readonly MediaKind[],
  handlers: { onGranted: () => void; onBlockedChange?: (blocked: boolean) => void }
): PermissionWatch {
  const { onGranted, onBlockedChange } = handlers;
  let done = false;
  let statuses: PermissionStatus[] = [];
  const cleanups: Array<() => void> = [];

  const cancel = () => {
    done = true;
    while (cleanups.length > 0) cleanups.pop()!();
  };

  void (async () => {
    const found = await Promise.all(kinds.map(queryPermissionStatus));
    if (done || found.length === 0) return;
    // A device that does not answer still lets the others report a block…
    statuses = found.filter((s): s is PermissionStatus => s !== null);
    // …but a grant can only be observed when every device can say so.
    if (statuses.length !== found.length) return;
    const check = (fromChange: boolean) => {
      if (done) return;
      onBlockedChange?.(statuses.some((s) => s.state === 'denied'));
      // Only a *change* to granted is a grant we did not already know about.
      if (fromChange && statuses.every((s) => s.state === 'granted')) {
        cancel();
        onGranted();
      }
    };
    const onChange = () => check(true);
    statuses.forEach((status) => {
      status.addEventListener('change', onChange);
      cleanups.push(() => status.removeEventListener('change', onChange));
    });
    check(false);
  })();

  return {
    isBlocked: () => !done && statuses.some((s) => s.state === 'denied'),
    cancel,
  };
}

/**
 * Whether every requested device already has a grant, judged by device labels.
 *
 * Labels are blank until the origin has been granted access, which makes them a
 * usable grant signal in browsers that will not answer `permissions.query`.
 * Returns `null` when even this cannot be determined.
 */
async function grantedByDeviceLabels(kinds: readonly MediaKind[]): Promise<boolean | null> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return null;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return kinds.every((kind) =>
      devices.some((d) => d.kind === deviceKind(kind) && d.label !== '')
    );
  } catch {
    return null;
  }
}

/**
 * Call `onGranted` once every requested device is granted — now, or whenever the
 * customer says yes.
 *
 * This is what lets the capture timeout mean "the device is not answering"
 * instead of "the human is not answering": the clock only starts once the
 * permission is in hand. Three cases, in order of how much the browser will tell
 * us:
 *
 *   1. `permissions.query` works — read the state, and subscribe to `change` so a
 *      grant that arrives a minute later still starts the clock.
 *   2. It does not (Firefox) — fall back to device labels and re-check on an
 *      interval, which detects the grant a second or so after it happens.
 *   3. Nothing is observable at all — never call back. The caller then has no
 *      bound on this attempt, which is the behaviour that predates the timeout
 *      entirely; arming on an unobservable permission would put the ten-second
 *      cut-off back in front of the customer, which is the whole bug.
 *
 * Returns a cancel function; callers must call it once the attempt settles.
 */
export function whenCaptureGranted(
  kinds: readonly MediaKind[],
  onGranted: () => void
): () => void {
  let done = false;
  const cleanups: Array<() => void> = [];

  const runCleanups = () => {
    while (cleanups.length > 0) cleanups.pop()!();
  };

  const fire = () => {
    if (done) return;
    done = true;
    runCleanups();
    onGranted();
  };

  if (kinds.length === 0) {
    // Nothing was asked for, so nothing can be pending on a human.
    fire();
    return () => undefined;
  }

  void (async () => {
    const statuses = await Promise.all(kinds.map(queryPermissionStatus));
    if (done) return;

    if (statuses.every((s): s is PermissionStatus => s !== null)) {
      const check = () => {
        if (statuses.every((s) => s.state === 'granted')) fire();
      };
      statuses.forEach((status) => {
        status.addEventListener('change', check);
        cleanups.push(() => status.removeEventListener('change', check));
      });
      check();
      return;
    }

    const labelled = await grantedByDeviceLabels(kinds);
    if (done) return;
    if (labelled === null) {
      // No permission API and no device list: we cannot tell a pending dialog
      // from a silent device, and only one of those two mistakes is one the
      // customer sees.
      return;
    }
    if (labelled) {
      fire();
      return;
    }

    const poll = async () => {
      if (done) return;
      if (await grantedByDeviceLabels(kinds)) fire();
    };
    const intervalId = setInterval(() => void poll(), GRANT_POLL_INTERVAL_MS);
    cleanups.push(() => clearInterval(intervalId));
  })();

  return () => {
    done = true;
    runCleanups();
  };
}
